// Per-org credit lock. Client-side only for now — when set, spend
// paths (councilChat, modelChat, regenerateAgentAvatar) throw an
// ApiError before hitting the backend, and the balance chip pulses
// to show the state. Backend-side enforcement follows when the
// spend-path infrastructure is redesigned alongside the orchestrator.
//
// State is persisted to localStorage keyed by org so switching orgs
// preserves per-org intent. Changes fan out via a custom event so
// every component can react (balance pulse, popout toggle, etc.)
// without prop-drilling.

const KEY = (orgId: number) => `wt_credits_locked_${orgId}`;
const EVENT = "wt:credits-lock-change";

export function isCreditsLocked(orgId: number | null | undefined): boolean {
  if (orgId == null || typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(KEY(orgId)) === "1";
  } catch {
    return false;
  }
}

export function setCreditsLocked(orgId: number, locked: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (locked) {
      window.localStorage.setItem(KEY(orgId), "1");
    } else {
      window.localStorage.removeItem(KEY(orgId));
    }
  } catch {
    // localStorage unavailable (private mode, quota) — fall back to
    // in-session only via the dispatched event.
  }
  window.dispatchEvent(
    new CustomEvent(EVENT, { detail: { orgId, locked } }),
  );
}

export function onCreditsLockChange(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => cb();
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}

/** Matches Tailwind @vendor/model mentions in a message body. Used
 * by chat-tab callers to decide whether a post could end up spending
 * credits (@vendor/model = direct model call; plain seat_key =
 * persona call via the orchestrator path). */
export const MODEL_MENTION_RE = /@[a-z0-9_-]+\/[a-z0-9._-]+/gi;

export function bodyHasModelMention(body: string): boolean {
  MODEL_MENTION_RE.lastIndex = 0;
  return MODEL_MENTION_RE.test(body);
}

export function extractModelMentions(body: string): string[] {
  MODEL_MENTION_RE.lastIndex = 0;
  const out: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = MODEL_MENTION_RE.exec(body)) !== null) {
    out.push(match[0].slice(1)); // strip the leading @
  }
  return out;
}
